import * as React from 'react';
import { IRouter } from 'react-router';
import { url, submitForm } from '../util';
import { NotEmpty } from './form/Constraints';
import { IInputChangeHandler, IFieldError, IError, IRouterContext } from '../types';
import { auth } from '../util/auth';

interface ILoginPageState {
  username?: string;
  password?: string;
  error?: IError;
  loading?: boolean;
}

export default class LoginPage extends React.Component<any, ILoginPageState> {
  context: IRouterContext;
  private _isMounted: boolean = false;

  static contextTypes = {
    router: React.PropTypes.object.isRequired
  };

  constructor(props: any) {
    super(props);
    this.onInputChange = this.onInputChange.bind(this);
    this.onSubmit = this.onSubmit.bind(this);

    this.state = {
      username: '',
      password: '',
      loading: false
    };
  }

  componentDidMount() {
    this._isMounted = true;
  }

  componentWillUnmount() {
    this._isMounted = false;
  }

  onSubmit(event) {
    event.preventDefault();

    const { username, password } = this.state;

    if (!username || !password) {
      this.setState({
        error: {
          fieldErrors: {
            username: !username ? { field: 'username', message: 'Username is required' } : null,
            password: !password ? { field: 'password', message: 'Password is required' } : null
          }
        }
      });
      return;
    }

    this.setState({ loading: true });

    submitForm('POST', '/api/login', { username, password }, (status, response) => {
      if (!this._isMounted) {
        return;
      }

      this.setState({ loading: false });
      if (status === 200) {
        // Login successful - save auth state and redirect to welcome page
        // response should have username, enabled, and roles array
        // Store auth state including password for Basic Auth
        auth.setAuth({
          username: response.username,
          password: password, // Store password for Basic Auth
          enabled: response.enabled !== undefined ? response.enabled : true,
          roles: response.roles || []
        });
        // Use window.location to avoid React update conflicts and ensure menu updates
        setTimeout(() => {
          window.location.href = '/';
        }, 100);
      } else if (status === 401) {
        // Invalid credentials
        if (this._isMounted) {
          this.setState({
            error: {
              fieldErrors: {},
              message: 'Invalid username or password'
            }
          });
        }
      } else if (status === 0) {
        // Network error (connection refused, CORS, etc.)
        if (this._isMounted) {
          this.setState({
            error: {
              fieldErrors: {},
              message: response.message || 'Unable to connect to server. Please ensure the backend is running on port 9966.'
            }
          });
        }
      } else {
        // Other error
        if (this._isMounted) {
          this.setState({
            error: {
              fieldErrors: {},
              message: response.message || 'An error occurred during login'
            }
          });
        }
      }
    });
  }

  onInputChange(name: string, value: string, fieldError: IFieldError) {
    const { error } = this.state;
    
    // Validate using NotEmpty constraint if no fieldError provided
    if (fieldError === null && name === 'username') {
      const isValid = NotEmpty.validate(value);
      fieldError = isValid === false ? { field: name, message: NotEmpty.message } : null;
    }
    
    const newFieldErrors = error ? Object.assign({}, error.fieldErrors, {[name]: fieldError }) : {[name]: fieldError };
    this.setState({
      [name]: value,
      error: { fieldErrors: newFieldErrors }
    });
  }

  render() {
    const { username, password, error, loading } = this.state;
    const loginData = { username, password };

    return (
      <div style={{ maxWidth: '600px', margin: '0 auto' }}>
        <h2>Login</h2>
        <p style={{ marginBottom: '20px' }}>Enter your credentials to access the application.</p>
        <form className='form-horizontal' method='POST' onSubmit={this.onSubmit}>
          {error && error.message && (
            <div className='alert alert-danger' role='alert' style={{ marginBottom: '20px' }}>
              {error.message}
            </div>
          )}
          <div className={`form-group has-feedback ${error && error.fieldErrors && error.fieldErrors.username ? 'has-error' : ''}`}>
            <label className='col-sm-2 control-label'>Username</label>
            <div className='col-sm-10' style={{ maxWidth: '400px' }}>
              <input 
                type='text' 
                name='username' 
                className='form-control' 
                value={username || ''} 
                onChange={(e) => this.onInputChange('username', e.target.value, null)} 
              />
              {username && (!error || !error.fieldErrors || !error.fieldErrors.username) && (
                <span className='glyphicon glyphicon-ok form-control-feedback' aria-hidden='true'></span>
              )}
              {error && error.fieldErrors && error.fieldErrors.username && (
                <span>
                  <span className='glyphicon glyphicon-remove form-control-feedback' aria-hidden='true'></span>
                  <span className='help-block'>{error.fieldErrors.username.message}</span>
                </span>
              )}
            </div>
          </div>
          <div className={`form-group has-feedback ${error && error.fieldErrors && error.fieldErrors.password ? 'has-error' : ''}`}>
            <label className='col-sm-2 control-label'>Password</label>
            <div className='col-sm-10' style={{ maxWidth: '400px' }}>
              <input 
                type='password' 
                name='password' 
                className='form-control' 
                value={password || ''} 
                onChange={(e) => this.onInputChange('password', e.target.value, null)} 
              />
              {password && (!error || !error.fieldErrors || !error.fieldErrors.password) && (
                <span className='glyphicon glyphicon-ok form-control-feedback' aria-hidden='true'></span>
              )}
              {error && error.fieldErrors && error.fieldErrors.password && (
                <span>
                  <span className='glyphicon glyphicon-remove form-control-feedback' aria-hidden='true'></span>
                  <span className='help-block'>{error.fieldErrors.password.message}</span>
                </span>
              )}
            </div>
          </div>
          <div className='form-group'>
            <div className='col-sm-offset-2 col-sm-10'>
              <button 
                type='submit' 
                className='btn btn-default' 
                disabled={loading}
                style={{ minWidth: '100px' }}
              >
                {loading ? 'Logging in...' : 'Login'}
              </button>
            </div>
          </div>
        </form>
      </div>
    );
  }
}
